import type { TranslationKey } from '@/i18n';
import type { AuthErrorKeyMap } from '../types/auth';
import { generalAuthErrorConfig } from '@/config/errors/general/generalAuthErrorConfig';

export function createGenericErrorHandler(
    operationConfig: AuthErrorKeyMap,
    generalConfig: AuthErrorKeyMap = generalAuthErrorConfig,
) {
    return function handleError(error: { status?: number; code?: string }): TranslationKey {
        const { status, code } = error;

        // Step 1
        if (operationConfig?.value) {
            // If both status and code are available, try to find exact match
            if (status && code) {
                const statusObj = operationConfig.value[status];

                // Will return if HTTP status and error code are available and match
                if (statusObj && typeof statusObj === 'object' && statusObj[code]) {
                    return statusObj[code] as TranslationKey;
                }
            }

            // If HTTP status and error code are not available or not found, check operation default
            if (operationConfig.value.default && typeof operationConfig.value.default === 'string') {
                const defaultValue = operationConfig.value.default;
                if (defaultValue && defaultValue.trim() !== '') {
                    return defaultValue as TranslationKey;
                }
            }
        }

        // Step 2
        if (generalConfig?.value) {
            // If HTTP status is available
            if (status) {
                const generalStatusObj = generalConfig.value[status];
                if (generalStatusObj && typeof generalStatusObj === 'object') {
                    // If error code is available
                    if (code && generalStatusObj[code]) {
                        return generalStatusObj[code] as TranslationKey;
                    }
                }
            }

            // HTTP status and error code are not available. We will use default value
            if (generalConfig.value.default && typeof generalConfig.value.default === 'string') {
                const generalDefault = generalConfig.value.default;
                if (generalDefault && generalDefault.trim() !== '') {
                    return generalDefault as TranslationKey;
                }
            }
        }

        // If everything else fails we will use the below value
        return 'errors.frontend.general' as TranslationKey;
    };
}

export function createErrorHandler(operationConfig: AuthErrorKeyMap, generalConfig?: AuthErrorKeyMap) {
    return createGenericErrorHandler(operationConfig, generalConfig);
}
